export type DHEditorCallback<T = undefined> = (error: Error | null, data?: T) => void
export type DHEditorSubscriberCallback<T = undefined> = (editor: DHEditorInstance, data?: T) => void

export interface DHListResponse<T> {
  page: number
  limit: number
  total: number
  items: T[]
}

export enum DHUserMode {
  GUEST = 'guest',
  VISITOR = 'visitor'
}

export type DHLibConfig = {
  domain?: string
  client_id?: string
  template_customization_object?: { classes: DHTco }
} & ({ [DHUserMode.GUEST]?: true } | { [DHUserMode.VISITOR]?: true })

/**
 * The Design Huddle editor library made available on the window object
 * as `window.DSHDEditorLib`.
 */
export interface DHEditorLib {
  configure: (config: DHLibConfig) => void
  createProject: (
    templateData: { template_id: string | string[] | undefined },
    callback: DHEditorCallback<DHEditorCreatedProjectData>
  ) => void
  getProjects: (
    query: DHProjectsListParams,
    callback: DHEditorCallback<DHListResponse<DHProject>>
  ) => void
  insert: (
    containerId: string,
    projectData: { project_id: string },
    callback: DHEditorCallback<DHEditorInstance>
  ) => void
  getVariableTemplatePreviewURL: (
    templateData: DHEditorCustomizationsObject,
    callback: DHEditorCallback<string>
  ) => void
  getGuestAccessToken: (config: DHLibConfig, callback: DHEditorCallback<string>) => void
  getVisitorAccessToken: (config: DHLibConfig, callback: DHEditorCallback<string>) => void
}

/**
 * The Design Huddle editor instance made available within the callback of
 * {@link DHEditorLib.insert}.
 */
export interface DHEditorInstance {
  changeElements: (tco: DHElementsTco) => void
  getProjectData: (
    query: {
      /**
       * If a thumbnail has not already been generated incorporating the latest design changes,
       * this will synchronously create a new thumbnail prior to returning the requested project data,
       * including the new thumbnail_url.
       */
      generate_latest_thumbnail?: boolean
    },
    callback: DHEditorCallback<DHElementsData>
  ) => void
  getRender: (
    options: Record<string, never>,
    callback: DHEditorCallback<DHProjectGetRenderResult>
  ) => void
  /**
   * Remove the current editor instance.
   */
  remove: () => void
  handleElementSelection: (callback: DHEditorSubscriberCallback<DHElement>) => void
  handleProjectDataChange: (callback: DHEditorSubscriberCallback) => void
  refreshAccessToken: (token: string, callback?: DHEditorSubscriberCallback) => void
  handleUnauthenticated: (callback: DHEditorSubscriberCallback) => void
}

/**
 * @see https://api.designhuddle.com/doc/Variable_Template_Previews.pdf
 */
export interface DHEditorCustomizationsObject {
  template_id: string
  template_code?: string
  customizations_hash?: string
  customizations?: { classes: DHTco }
  width?: number
  height?: number
  fit?: string
}

export type DHEditorGetVariableTemplatePreviewURLCallback = (
  error: Error | null,
  image_url: string
) => void

export interface DHEditorCreatedProjectData {
  customizations_hash: string
  project_id: string
}

export interface DHColor {
  color: string | undefined
}

export interface DHText {
  text: string | undefined
  auto_fit?: boolean
}

export interface DHUrl {
  url: string | undefined
}

export interface DHZone {
  zone_type: string
  width: number
  height: number
}

export type DHCustomizationValue = DHColor | DHText | DHUrl

export interface DHTco {
  [key: string]: DHCustomizationValue
  flowcodeImage: DHUrl
}

export interface DHElementsTco {
  elements: Record<string, DHCustomizationValue>
}

export interface DHElementsData {
  project_id: string
  project_title: string
  media_type: string
  pages: DHPage[]
  current_page_id: string
}

export interface DHPage {
  page_id: string
  dimensions: DHDimensions
  background_color: string
  elements: DHElements
}

export interface DHDimensions {
  width: number
  height: number
  unit_type_id: string
}

export interface DHElements {
  [key: string | number | symbol]: DHElement
}

export interface DHElement {
  element_name: null
  element_classes: string[]
  template_element: boolean
  type: string
  file_type?: string
  vector_type?: string
  width?: number
  height?: number
  source_width?: number
  source_height?: number
  text?: string
  colors?: string[]
  path_count?: number
  line_count?: number
  asset_id?: number
  asset_code?: string
  flip_vertical?: boolean
  zone?: DHZone
}

export interface DHProject {
  project_title: string
  page_count: number
  date_created: Date
  date_updated: Date
  last_edited: Date
  last_opened: Date | null
  user: DHUser
  source_template: DHSourceTemplate
  project_id: string
  thumbnail_url: string
  media_type: DHMediaType
  project_status: DHProjectStatus
  dimensions: DHDimensions
}

export interface DHDimensions {
  width: number
  height: number
  unit_type: DHUnitType
}

export interface DHUnitType {
  unit_type_id: DHUnitTypeID
  unit_type_name: DHUnitTypeName
}

export enum DHUnitTypeID {
  In = 'in',
  Px = 'px',
  Mm = 'mm'
}

export enum DHUnitTypeName {
  In = 'Inches',
  px = 'Pixels',
  Mm = 'Millimeters'
}

export interface DHMediaType {
  media_type_id: DHMediaTypeID
  media_type_name: DHMediaTypeName
}

export enum DHMediaTypeID {
  Digital = 'digital',
  Presentation = 'presentation',
  Print = 'print',
  Video = 'video'
}

export enum DHMediaTypeName {
  Digital = 'Digital',
  Presentation = 'Presentation',
  Print = 'Print',
  Video = 'Video'
}

export interface DHProjectStatus {
  project_status_id: DHProjectStatusID
  project_status_name: DHProjectStatusName
}

export interface DHProjectApprovalLog {
  comment: string
  date_created: string
}

export interface DHProjectApprovalStatus {
  project_approval_status_id: string
  project_approval_status_name: string
  project_approval_log: DHProjectApprovalLog
}

export enum DHProjectStatusID {
  Active = 'active',
  Archived = 'archived'
}

export enum DHProjectStatusName {
  Active = 'Active',
  Archived = 'Archived'
}

export interface DHSourceTemplate {
  source_template_id: number
  source_template_code: null
  source_template_title: string
  primary_template_category_id: number
  primary_template_category_item_id: number
}

export interface DHUser {
  user_id: number
  email_address: null
  first_name: string
  last_name: string
  current_user: boolean
}

export interface DHProjectsListParams {
  /** Specify the media type to filter the result by. */
  media_type?: DHMediaTypeID
  /** Specify the media types to filter the result by. */
  media_types?: DHMediaTypeID[]
  /** Specify the project status to filter the result by. */
  project_status?: DHProjectStatusID
  /** Specify the project statuses to filter the result by. */
  project_statuses?: DHProjectStatusID[]
  /** Specify the project ownership to filter the result by. */
  project_ownership?: 'all_projects' | 'my_projects' | 'shared_with_me'
  /** Specify the Source Template ID to filter the result by. */
  source_template_id?: number
  /** Specify the Source Template IDs to filter the result by. */
  source_template_ids?: number[]
  /** Specify the Source Template Code to filter the result by. */
  source_template_code?: string
  /** Specify the Source Template Codes to filter the result by. */
  source_template_codes?: string[]
  /** Specify the width to filter the result by. */
  width?: number
  /** Specify the height to filter the result by. */
  height?: number
  /** Specify the unit type to filter the result by. */
  unit_type?: DHUnitTypeID
  /** A search term used to narrow the focus of the result. */
  search?: string
  /** Specify which attribute to order the result by. */
  sort?: string
  /** Specify whether the result should be retrieved in ascending or descending order. */
  order?: 'ASC' | 'DESC'
  /** Specify the number of items per page to return. */
  limit?: number
  /** Specify which page of the result to return. */
  page?: number
  /** Specify the fields to the retrieve. */
  fields?: (keyof DHProject)[]
}

export interface DHProjectGetRenderResult extends Pick<DHDimensions, 'height' | 'width'> {
  page_id: string
  /** A base64-encoded data URL of the rendered project image in PNG format */
  url: string
}

export interface DHAPIPage {
  page_id: string
  page_name?: string
  page_number: number
  scene_slides?: Array<{
    scene_slide_id: string
    scene_slide_number: number
    thumbnail_url: string
    date_created: string
  }>
  thumbnail_url: string
  date_created: string
}

export interface DHAPIProjectResponse {
  success: boolean
  data: {
    project_id: string
    user: {
      user_id: number
      email_address: string
      first_name: string
      last_name: string
    }
    project_title: string
    dimensions: DHDimensions
    thumbnail_url: string
    source_template?: {
      source_template_id: number
      source_template_code?: string
      source_template_title: string
    }
    media_type: DHMediaType
    project_status: DHProjectStatus
    project_approval_status?: DHProjectApprovalStatus
    pages: Array<DHAPIPage>
    date_created: string
    date_updated: string
    last_edited?: string
    last_opened?: string
  }
}
